'use client';

import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

const faqItems = [
    { questionKey: 'FAQ.items.0.question', answerKey: 'FAQ.items.0.answer' },
    { questionKey: 'FAQ.items.1.question', answerKey: 'FAQ.items.1.answer' },
    { questionKey: 'FAQ.items.2.question', answerKey: 'FAQ.items.2.answer' },
    { questionKey: 'FAQ.items.3.question', answerKey: 'FAQ.items.3.answer' },
    { questionKey: 'FAQ.items.4.question', answerKey: 'FAQ.items.4.answer' },
];

function AccordionItem({
    question,
    answer,
    isOpen,
    onToggle,
}: {
    question: string;
    answer: string;
    isOpen: boolean;
    onToggle: () => void;
}) {
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!contentRef.current) return;
        if (isOpen) {
            gsap.to(contentRef.current, {
                height: 'auto',
                opacity: 1,
                duration: 0.4,
                ease: 'power3.out',
            });
        } else {
            gsap.to(contentRef.current, {
                height: 0,
                opacity: 0,
                duration: 0.3,
                ease: 'power3.in',
            });
        }
    }, [isOpen]);

    return (
        <div className="border-b border-slate-200 dark:border-border/60 py-2 sm:py-4">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between py-5 text-left group gap-4"
                aria-expanded={isOpen}
            >
                <span className="text-base sm:text-xl font-medium text-slate-900 dark:text-white group-hover:text-primary transition-colors max-w-4xl leading-tight">
                    {question}
                </span>

                {/* Circular toggle button inspired by Crawford */}
                <div
                    className={cn(
                        "flex-shrink-0 h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm",
                        "bg-[#506C83] hover:bg-[#435b6e] text-white"
                    )}
                >
                    <div className="relative h-4 w-4 sm:h-5 sm:w-5">
                        {/* Horizontal line (always visible) */}
                        <div className="absolute top-1/2 left-0 w-full h-[2px] bg-white -translate-y-1/2" />
                        {/* Vertical line (rotates/scales to minus) */}
                        <div
                            className={cn(
                                "absolute top-0 left-1/2 h-full w-[2px] bg-white -translate-x-1/2 transition-all duration-300 ease-in-out",
                                isOpen ? "rotate-90 opacity-0 scale-y-0" : "rotate-0 opacity-100 scale-y-100"
                            )}
                        />
                    </div>
                </div>
            </button>
            <div ref={contentRef} className="overflow-hidden" style={{ height: 0, opacity: 0 }}>
                <div className="pb-8">
                    <p className="text-sm sm:text-base text-slate-700 dark:text-muted-foreground max-w-3xl leading-relaxed font-normal">
                        {answer}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function FAQSection() {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLElement>(null);
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    useEffect(() => {
        if (!sectionRef.current) return;
        gsap.set(sectionRef.current, { opacity: 0 });
        gsap.to(sectionRef.current, {
            opacity: 1,
            duration: 0.5,
        });
    }, []);

    // Generate FAQ JSON-LD for rich results
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: t(item.questionKey),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t(item.answerKey),
            },
        })),
    };

    return (
        <section ref={sectionRef} className="mb-20 sm:mb-28">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
            />

            <div className="text-center mb-10 sm:mb-16 max-w-2xl mx-auto">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#506C83] dark:text-[#c7d5e0] mb-3">
                    {t('FAQ.sectionLabel')}
                </p>
                <h2 className="text-3xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
                    {t('FAQ.sectionTitle')}
                </h2>
                <div className="h-1 w-12 bg-[#506C83] mx-auto rounded-full mb-6" />
                <p className="text-base sm:text-lg text-slate-600 dark:text-muted-foreground px-4">
                    {t('FAQ.sectionSubtitle')}
                </p>
            </div>

            <div className="max-w-6xl mx-auto border-t border-slate-200 dark:border-border/60">
                {faqItems.map((item, index) => (
                    <AccordionItem
                        key={item.questionKey}
                        question={t(item.questionKey)}
                        answer={t(item.answerKey)}
                        isOpen={openIndex === index}
                        onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                    />
                ))}
            </div>
        </section>
    );
}
